import { useState } from "react";
import styled from "styled-components";
import { ProxiedRoutes } from "@lifeat/shared/system/RouteConstants";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { LifeAtTextInputV2 } from "@common/LifeAtTextInput/LifeAtTextInputV2";
import GoogleLogo from "@resources/logos/integrations/Google/google_logo.png";
import { FontSize } from "@themes/Fonts";
import Styles from "@themes/Styles";
import { AuthResult, useAppAuth } from "@user/useAppAuth";

type Mode = "sign-in" | "sign-up";

const Container = styled.div`
    max-width: 320px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
`;

const Header = styled.h1`
    color: ${({ theme }) => theme.textColorPrimary};
    font-size: ${FontSize.large};
    font-weight: bold;
    margin-bottom: 40px;
`;

const SocialLoginButton = styled(LifeAtButtonV2)`
    & .lifeat-button-content {
        padding: 12px 16px;
    }
`;

const Logo = styled.img`
    width: 17px;
    height: auto;
    margin-right: 8px;
    object-fit: contain;
`;

const Divider = styled.div`
    display: flex;
    align-items: center;
    text-align: center;
    color: ${({ theme }) => theme.textColorSecondary};
    font-size: ${FontSize.normal};
    margin: 20px 0;

    &::before,
    &::after {
        content: "";
        flex: 1;
        border-bottom: 1px solid ${({ theme }) => theme.borderPrimary};
    }

    &::before {
        margin-right: 10px;
    }

    &::after {
        margin-left: 10px;
    }
`;

const ErrorSection = styled.p`
    font-size: ${FontSize.normal};
    color: red;

    & a {
        cursor: pointer;
        font-weight: bold;
        text-decoration: underline;
    }
`;

const StyledTextInput = styled(LifeAtTextInputV2)`
    margin-bottom: 12px;
`;

const ContinueButton = styled(LifeAtButtonV2)`
    margin-top: 8px;
`;

const Notice = styled.p`
    margin-top: 12px;
    font-size: 14px;
    color: ${({ theme }) => theme.textColorSecondary};
    & a {
        color: ${Styles.buttonFillBlue};
    }
`;

const ModeSwitch = styled.div`
    margin-top: 12px;
    font-size: 14px;
    color: ${({ theme }) => theme.textColorSecondary};
`;

const ModeSwitchButton = styled.span`
    cursor: pointer;
    color: ${Styles.buttonFillBlue};
    text-decoration: underline;
`;

type Props = {
    className?: string;
    onSuccessRedirectUrl?: string;
};

export const AuthForm = (props: Props) => {
    const [mode, setMode] = useState<Mode>("sign-up");
    const { signInWithEmail, signUpWithEmail, sendPasswordResetEmail } = useAppAuth(props);
    const [authResult, setAuthResult] = useState<AuthResult | null>(null);

    const handleEmailSignIn = async () => {
        const email = (document.getElementById("email") as HTMLInputElement).value;
        const password = (document.getElementById("password") as HTMLInputElement).value;
        const result = await signInWithEmail(email, password);
        setAuthResult(result);
    };

    const handleEmailSignUp = async () => {
        const displayName = (document.getElementById("displayName") as HTMLInputElement).value;
        const email = (document.getElementById("email") as HTMLInputElement).value;
        const password = (document.getElementById("password") as HTMLInputElement).value;
        const result = await signUpWithEmail(email, password, displayName);
        setAuthResult(result);
    };

    return (
        <Container className={props.className}>
            <Header>
                {mode === "sign-in" && <span>Sign in to LifeAt</span>}
                {mode === "sign-up" && <span>Create a LifeAt account</span>}
            </Header>
            <SocialLoginButton variant="outline" fullWidth href={calculateGoogleSignInUrl()}>
                <Logo src={GoogleLogo} />
                {mode === "sign-in" && <span>Sign in</span>}
                {mode === "sign-up" && <span>Sign up</span>}
                <span> with Google</span>
            </SocialLoginButton>
            <Divider>or</Divider>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    switch (mode) {
                        case "sign-in":
                            handleEmailSignIn();
                            break;
                        case "sign-up":
                            handleEmailSignUp();
                            break;
                    }
                }}
            >
                {authResult?.success === false && (
                    <ErrorSection>
                        {authResult.error === "email-already-in-use" && (
                            <span>
                                <span>You already have an account. Click </span>
                                <a
                                    onClick={() => {
                                        setMode("sign-in");
                                        setAuthResult(null);
                                    }}
                                >
                                    here
                                </a>
                                <span> to sign in.</span>
                            </span>
                        )}
                        {authResult.error === "user-not-found" && (
                            <span>
                                <span>Could not find that account. Click </span>
                                <a
                                    onClick={() => {
                                        setMode("sign-up");
                                        setAuthResult(null);
                                    }}
                                >
                                    here
                                </a>
                                <span> to sign up.</span>
                            </span>
                        )}
                        {authResult.error === "wrong-password" && (
                            <span>
                                <span>Wrong password. Did you forget your password? Click </span>
                                <a
                                    onClick={() => {
                                        const email = (document.getElementById("email") as HTMLInputElement).value;
                                        sendPasswordResetEmail(email);
                                    }}
                                >
                                    here
                                </a>
                                <span> to reset password.</span>
                            </span>
                        )}
                        {authResult.error === "unknown" && <span>{authResult.message}</span>}
                    </ErrorSection>
                )}
                {mode === "sign-up" && (
                    <StyledTextInput type="text" id="displayName" label="Name" required showBorder />
                )}
                <StyledTextInput type="email" id="email" label="Email" required showBorder />
                <StyledTextInput type="password" id="password" label="Password" required showBorder />
                <ContinueButton
                    variant="solid"
                    fullWidth
                    overrideTextColor={"#FFF"}
                    overrideBackgroundColor={Styles.buttonFillBlue}
                    disableHover={true}
                >
                    Continue
                </ContinueButton>
            </form>
            <Notice>
                <span>By continuing, you are indicating that you accept our </span>
                <a href={ProxiedRoutes.Terms} target="_blank">
                    Terms of Service
                </a>
                <span> and </span>
                <a href={ProxiedRoutes.Privacy} target="_blank">
                    Privacy Policy
                </a>
            </Notice>
            <ModeSwitch>
                {mode === "sign-in" && (
                    <>
                        <span>Don't have an account? </span>
                        <ModeSwitchButton onClick={() => setMode("sign-up")}>Sign up</ModeSwitchButton>
                    </>
                )}
                {mode === "sign-up" && (
                    <>
                        <span>Already have an account? </span>
                        <ModeSwitchButton onClick={() => setMode("sign-in")}>Sign in</ModeSwitchButton>
                    </>
                )}
            </ModeSwitch>
        </Container>
    );
};

function calculateGoogleSignInUrl() {
    const params = new URLSearchParams({
        response_type: "code",
        redirect_uri: new URL("/oauth/google/callback", window.location.origin).href,
        prompt: "select_account consent",
        state: JSON.stringify({ shouldGoBackToDesktop: !!window.todesktop }),
        client_id: process.env.GOOGLE_CALENDAR_CLIENT_ID,
        scope: "openid https://www.googleapis.com/auth/userinfo.email profile",
    });

    return `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
}
